import { api, handleApiError } from './api';

// Interface for a single meeting object (align with backend response)
export interface Meeting {
  id: number | string; // Updated to allow string as well
  meeting_id: string; // Session ID - always present
  conversation_id: string | string[] | null; // Actual conversation ID from customer data - can be null or array of IDs
  request_id?: string | null; // UUID for processing request - can be null
  name: string;
  email: string;
  client: string;
  clientId?: string | number; // Made optional since it can be missing
  date: string; // ISO 8601 string
  project: string;
  salesExecutive: string;
  salesExecutiveId: string | number; // Added: Unique identifier for the sales executive
  duration?: string; // e.g., "90 min" or number of minutes
  note?: string;
  status?: 'Scheduled' | 'Completed' | 'Cancelled' | string; // Allow other statuses
  scorePercentage?: number | null;
  pendingActions?: { // Added: Pending actions for the meeting
    count: number;
    items: Array<{ 
      id: string; 
      description: string; 
      isCompleted?: boolean; 
      dueDate?: string 
    }>; 
    // Or items: string[]; if a simpler structure is used by the backend
  } | Array<any>; // Allow empty array as well
  conversationDurations?: {
    [conversationId: string]: {
      duration: string;
      durationSec: any;
    };
  };
  // Add any other fields that your API will return for a meeting
}

// Interface for pagination data returned by the API
export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
}

// Interface for the overall API response structure
export interface MeetingsApiResponse {
  data: Meeting[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
  };
}

// Interface for the query parameters accepted by the getMeetings API
export interface MeetingApiParams {
  page?: number;
  limit?: number;
  salesExecutiveName?: string;
  salesExecutiveId?: string | number; // Added: Filter by sales executive ID
  projectId?: string;
  clientId?: string | number; // Allow filtering by clientId
  dateFrom?: string; // ISO 8601 string
  dateTo?: string;   // ISO 8601 string
  status?: string;
  sortBy?: string;   // e.g., 'date', 'client'
  sortOrder?: 'asc' | 'desc';
  type?: string; // Added for filtering by type, e.g., 'real_estate'
  search?: string; // Added for searching across multiple fields
  business_identifier?: string; // e.g., 'real_estate'
}

export interface UploadMeetingPdfPayload {
  conversationId: string;
  salespersonId: string | number;
  projectId: string;
  clientId: string | number;
}

class MeetingService {
  async getMeetings(params?: MeetingApiParams): Promise<MeetingsApiResponse> {
    try {
      const response = await api.get<MeetingsApiResponse>('/api/meetings', { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching meetings:", error);
      // Humanize and throw the error so it can be caught and displayed properly
      handleApiError(error);
      // This won't be reached, but TypeScript needs it
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          limit: 0
        }
      };
    }
  }

  /**
   * Fetch meetings for a single sales executive, scoped server-side by salesExecutiveId.
   * Use this for the sales-executive profile so the list and its total count come from
   * one source (response.pagination.totalItems) instead of filtering the global feed.
   */
  async getSalesPersonMeetings(
    salesExecutiveId: string | number,
    params?: Omit<MeetingApiParams, 'salesExecutiveId'>
  ): Promise<MeetingsApiResponse> {
    return this.getMeetings({
      type: 'real_estate',
      ...params,
      salesExecutiveId,
    });
  }

  /**
   * Check if a PDF for the given conversationId already exists on the server.
   * Called before upload: if exists, skip generation and upload; if not, proceed.
   * GET /api/dashboard/pdfs/{conversationId}?projectId=xxx
   * - 200 (with or without body): PDF exists → skip upload.
   * - 404: PDF does not exist → proceed with generate and upload.
   * - Body may include { exists: boolean } or { present: boolean } for explicit flag.
   */
  async checkPdfExistsByConversationId(conversationId: string, projectId?: string): Promise<boolean> {
    try {
      const response = await api.get<{
        exists?: boolean;
        present?: boolean;
      }>("/api/dashboard/pdfs", {
        params: {
          conversationId, // always send this
          ...(projectId ? { projectId } : {}), // optional projectId
        },
      });
  
      const data = response.data ?? {};
      if (data.exists === false || data.present === false) return false;
      if (data.exists === true || data.present === true) return true;
      return true;
    } catch (err: any) {
      if (err?.response?.status === 404) return false;
      return false;
    }
  }

  /**
   * Upload a meeting PDF to the dashboard API (background upload).
   * POST multipart/form-data to api/dashboard/pdfs/upload.
   * Backend should first check conversationId: if present, skip; if not present, proceed.
   */
  async uploadMeetingPdf(file: Blob | File, payload: UploadMeetingPdfPayload): Promise<void> {
    const formData = new FormData();
    // Ensure server accepts as PDF: use blob with correct type and a .pdf filename
    const pdfBlob = file instanceof File ? file : new Blob([file], { type: 'application/pdf' });
    const filename = file instanceof File && file.name ? file.name : `meeting-${payload.conversationId}.pdf`;
    formData.append('file', pdfBlob, filename);
    formData.append('conversationId', payload.conversationId);
    formData.append('salespersonId', String(payload.salespersonId));
    formData.append('projectId', payload.projectId);
    formData.append('clientId', String(payload.clientId));
    await api.post('/api/dashboard/pdfs/upload', formData);
  }

  /**
   * Fetch all meetings from the API in one go (paginates until no more pages).
   */
  // async getAllMeetings(params?: Omit<MeetingApiParams, 'page'>): Promise<Meeting[]> {
  //   const all: Meeting[] = [];
  //   const limit = params?.limit ?? 10;
  //   let page = 1;
  //   let hasMore = true;
  //   while (hasMore) {
  //     const response = await this.getMeetings({ ...params, page, limit });
  //     if (response.data?.length) all.push(...response.data);
  //     hasMore = response.data?.length === limit && (response.pagination.currentPage < response.pagination.totalPages);
  //     page++;
  //     if (page > 50) break; // safety cap
  //   }
  //   return all;
  // }

  async getAllMeetings(
    params?: Omit<MeetingApiParams, 'page'>
  ): Promise<Meeting[]> {
    const limit = params?.limit ?? 10;
  
    try {
      const response = await this.getMeetings({
        ...params,
        page: 1, // Always force page 1
        limit,
      });
  
      return response.data ?? [];
    } catch (error) {
      console.error('[getAllMeetings] Error fetching page 1 meetings:', error);
      return [];
    }
  }
  

  /**
   * Find a meeting by conversationId by searching the meetings API (paginated).
   */
  async findMeetingByConversationId(conversationId: string, params?: { type?: string; business_identifier?: string; limit?: number }): Promise<Meeting | null> {
    const limit = params?.limit ?? 10;
    const type = params?.type ?? 'real_estate';
    const business_identifier = params?.business_identifier;
    for (let page = 1; page <= 10; page++) {
      const requestParams: MeetingApiParams = { type, page, limit, ...(business_identifier != null && { business_identifier }) };
      const response = await this.getMeetings(requestParams);
      if (!response.data?.length) break;
      const meeting = response.data.find((m) => {
        const ids = Array.isArray(m.conversation_id) ? m.conversation_id : m.conversation_id ? [m.conversation_id] : [];
        return ids.includes(conversationId);
      });
      if (meeting) return meeting;
      if (response.pagination.currentPage >= response.pagination.totalPages) break;
    }
    return null;
  }
}

export const meetingService = new MeetingService(); 